import React, { useEffect, useState, useCallback, useRef } from "react";
import { TrendingUp, TrendingDown, Target, Zap, Activity } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";

export default function VirtualPortfolioTab({ token }) {
  const [shadowData, setShadowData] = useState(null);
  const [oppsData, setOppsData] = useState(null);
  const [gauntletData, setGauntletData] = useState(null);
  const [goldData, setGoldData] = useState(null);
  const [macroData, setMacroData] = useState(null);
  const [sentData, setSentData] = useState(null);
  const [earnData, setEarnData] = useState(null);
  const [attrData, setAttrData] = useState(null);
  const [secData, setSecData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hoverIdx, setHoverIdx] = useState(null);
  const [flashTickers, setFlashTickers] = useState({});
  const prevPrices = useRef({});

  const fetchData = useCallback(async () => {
    if (!token) return;
    try {
      const [r1, r2, r3, r4, r5, r6, r7, r8, r9] = await Promise.all([
        fetch(`${API}/api/skynet-brain/stock-shadow-fleet`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/api/skynet-brain/sp500-opportunities`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/api/skynet-brain/alpha-gauntlet`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/api/skynet-brain/gold-alerts`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/api/skynet-brain/market-context`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/api/skynet-brain/news-sentiment`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/api/skynet-brain/earnings-calendar`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/api/skynet-brain/attribution`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/api/skynet-brain/sector-rotation`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (r1.ok) {
        const d = await r1.json();
        const flashes = {};
        if (d.active_deals) {
          for (const deal of d.active_deals) {
            const prev = prevPrices.current[deal.ticker];
            if (prev !== undefined && prev !== deal.current_price) {
              flashes[deal.ticker] = deal.current_price > prev ? "up" : "down";
            }
            prevPrices.current[deal.ticker] = deal.current_price;
          }
        }
        if (Object.keys(flashes).length > 0) {
          setFlashTickers(flashes);
          setTimeout(() => setFlashTickers({}), 1000);
        }
        setShadowData(d);
      }
      if (r2.ok) setOppsData(await r2.json());
      if (r3.ok) setGauntletData(await r3.json());
      if (r4.ok) setGoldData(await r4.json());
      if (r5.ok) setMacroData(await r5.json());
      if (r6.ok) setSentData(await r6.json());
      if (r7.ok) setEarnData(await r7.json());
      if (r8.ok) setAttrData(await r8.json());
      if (r9.ok) setSecData(await r9.json());
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
    const iv = setInterval(fetchData, 10000);  // refresh every 10s for live feel
    return () => clearInterval(iv);
  }, [fetchData]);

  if (loading) return <div className="text-zinc-400 p-6">Loading virtual portfolio…</div>;
  if (!shadowData || shadowData.error) return (
    <div className="text-zinc-400 p-6">
      <h3 className="text-lg font-bold text-orange-400 mb-2">Stock Shadow Fleet</h3>
      <p>{shadowData?.error || "No data yet. Bots initializing..."}</p>
    </div>
  );

  const fleet = shadowData.fleet || {};
  const tournament = shadowData.tournament || {};
  const activeDeals = shadowData.active_deals || [];
  const recentDeals = shadowData.recent_deals || [];
  const equityHistory = shadowData.equity_history || [];

  // Use TOURNAMENT total (DCA + Gauntlet) for hero, not just DCA fleet
  const initial = tournament.total_initial || fleet.initial || 10000;
  const nav = tournament.total_equity || fleet.equity || initial;
  const totalPnL = tournament.total_pnl || fleet.pnl_dollar || 0;
  const totalPnLPct = tournament.total_pnl_pct || fleet.pnl_pct || 0;
  const totalClosed = (shadowData.bots || []).reduce((s, b) => s + (b.deals_closed || 0), 0);
  const totalRealized = (shadowData.bots || []).reduce((s, b) => s + (b.total_realized_pnl || 0), 0);
  // Build NAV trajectory series — sum equity per bot per timestamp bucket
  const navByTime = {};
  for (const e of equityHistory) {
    const ts = e.t;
    if (!navByTime[ts]) navByTime[ts] = { ts, total: 0, n: 0 };
    navByTime[ts].total += e.equity || 0;
    navByTime[ts].n += 1;
  }
  // Only keep timestamps where we have equity for all bots (or at least 4)
  const navSeries = Object.values(navByTime)
    .filter(x => x.n >= 4)
    .map(x => ({ date: x.ts, value: x.total }))
    .sort((a, b) => a.date.localeCompare(b.date));

  // Chart prep (NAV trajectory from equity history)
  const chartW = 720, chartH = 220;
  const padL = 50, padR = 10, padT = 15, padB = 28;
  const innerW = chartW - padL - padR;
  const innerH = chartH - padT - padB;

  const navMin = navSeries.length > 0 ? Math.min(...navSeries.map(p => p.value), initial) : initial;
  const navMax = navSeries.length > 0 ? Math.max(...navSeries.map(p => p.value), initial) : initial;
  const ySpan = Math.max(navMax - navMin, initial * 0.005);
  const yMin = navMin - ySpan * 0.1;
  const yMax = navMax + ySpan * 0.1;
  const yRange = yMax - yMin;
  const xStep = navSeries.length > 1 ? innerW / (navSeries.length - 1) : 0;
  const navPath = navSeries.map((p, i) => {
    const x = padL + i * xStep;
    const y = padT + innerH - ((p.value - yMin) / yRange) * innerH;
    return `${i === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`;
  }).join(" ");

  const initY = padT + innerH - ((initial - yMin) / yRange) * innerH;

  return (
    <div className="space-y-4 px-3 md:px-0">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-zinc-800 pb-3">
        <div>
          <h2 className="text-xl font-bold text-orange-400 font-mono flex items-center gap-2">
            <TrendingUp className="w-5 h-5" /> Stocks Lab — DCA Shadow Bots
          </h2>
          <p className="text-xs text-zinc-500 font-mono">
            $10,000 across 20 DCA bots + 3 Gauntlet + 1 Scalper · real-time yfinance prices · paper-only · refreshes 10s
          </p>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase text-zinc-500 flex items-center gap-1">
            <Activity className="w-3 h-3 animate-pulse text-emerald-400" /> LIVE
          </div>
          <div className="text-xs font-mono text-zinc-300">
            {new Date().toLocaleTimeString()}
          </div>
        </div>
      </div>

      {/* MAIN HERO CARD */}
      <div className="bg-gradient-to-br from-orange-900/20 to-zinc-900/40 border-2 border-orange-500/30 rounded-xl p-5">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div>
            <div className="text-[10px] uppercase text-zinc-500">NAV</div>
            <div className={`text-3xl font-mono font-bold ${totalPnL >= 0 ? "text-emerald-400" : "text-red-400"}`}>
              ${nav.toFixed(2)}
            </div>
            <div className="text-[10px] text-zinc-500">from $10,000</div>
          </div>
          <div>
            <div className="text-[10px] uppercase text-zinc-500">P&L</div>
            <div className={`text-3xl font-mono font-bold ${totalPnL >= 0 ? "text-emerald-400" : "text-red-400"}`}>
              {totalPnL >= 0 ? "+" : ""}${totalPnL.toFixed(2)}
            </div>
            <div className={`text-xs font-mono ${totalPnLPct >= 0 ? "text-emerald-500" : "text-red-500"}`}>
              {totalPnLPct >= 0 ? "+" : ""}{totalPnLPct.toFixed(2)}%
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase text-zinc-500">Active Bots</div>
            <div className="text-3xl font-mono font-bold text-zinc-100">{fleet.n_in_deal}/{fleet.n_bots}</div>
            <div className="text-[10px] text-zinc-500">in deal</div>
          </div>
          <div>
            <div className="text-[10px] uppercase text-zinc-500">Closed Deals</div>
            <div className="text-3xl font-mono font-bold text-zinc-100">{totalClosed}</div>
            <div className={`text-[10px] font-mono ${totalRealized >= 0 ? "text-emerald-500" : "text-red-500"}`}>
              realized {totalRealized >= 0 ? "+" : ""}${totalRealized.toFixed(0)}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase text-zinc-500">Strategy</div>
            <div className="text-sm font-mono font-bold text-zinc-100">DCA + Trail</div>
            <div className="text-[9px] text-zinc-500">cron 5min · market hours</div>
          </div>
        </div>
      </div>

      {/* Stock Shadow Fleet — Active Deals */}
      {shadowData && shadowData.fleet && (
        <div className="bg-gradient-to-br from-blue-900/20 to-zinc-900/40 border-2 border-blue-500/30 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <Zap className="w-5 h-5 text-blue-400" />
            <h3 className="text-sm font-mono font-bold text-blue-400">
              📊 STOCK SHADOW FLEET — {shadowData.fleet.n_in_deal}/{shadowData.fleet.n_bots} active deals
            </h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <div className="bg-zinc-900/60 border border-zinc-800 rounded p-2">
              <div className="text-[10px] uppercase text-zinc-500">Fleet Initial</div>
              <div className="text-lg font-mono font-bold text-zinc-100">${shadowData.fleet.initial}</div>
            </div>
            <div className="bg-zinc-900/60 border border-zinc-800 rounded p-2">
              <div className="text-[10px] uppercase text-zinc-500">Fleet Equity</div>
              <div className={`text-lg font-mono font-bold ${shadowData.fleet.pnl_dollar >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                ${shadowData.fleet.equity}
              </div>
            </div>
            <div className="bg-zinc-900/60 border border-zinc-800 rounded p-2">
              <div className="text-[10px] uppercase text-zinc-500">Fleet P&L</div>
              <div className={`text-lg font-mono font-bold ${shadowData.fleet.pnl_dollar >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                {shadowData.fleet.pnl_dollar >= 0 ? "+" : ""}${shadowData.fleet.pnl_dollar}
              </div>
              <div className={`text-[10px] font-mono ${shadowData.fleet.pnl_pct >= 0 ? "text-emerald-500" : "text-red-500"}`}>
                {shadowData.fleet.pnl_pct >= 0 ? "+" : ""}{shadowData.fleet.pnl_pct?.toFixed(2)}%
              </div>
            </div>
            <div className="bg-zinc-900/60 border border-zinc-800 rounded p-2">
              <div className="text-[10px] uppercase text-zinc-500">Closed Deals</div>
              <div className="text-lg font-mono font-bold text-zinc-100">
                {(shadowData.bots || []).reduce((s, b) => s + (b.deals_closed || 0), 0)}
              </div>
            </div>
          </div>

          {/* Active Deals Table */}
          {shadowData.active_deals && shadowData.active_deals.length > 0 && (
            <div className="mt-4">
              <div className="text-[11px] uppercase text-zinc-500 font-mono mb-2">🔵 Currently Running Deals</div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs font-mono">
                  <thead>
                    <tr className="text-zinc-500 border-b border-zinc-800">
                      <th className="text-left py-2 pr-3">Ticker</th>
                      <th className="text-right pr-3">Entry $</th>
                      <th className="text-right pr-3">Cur $</th>
                      <th className="text-right pr-3">P&L %</th>
                      <th className="text-right pr-3">P&L $</th>
                      <th className="text-right pr-3">Inv $</th>
                      <th className="text-center pr-3">SOs</th>
                      <th className="text-right pr-3">Age</th>
                      <th className="text-right pr-3">TP target</th>
                      <th className="text-right">Closed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shadowData.active_deals.map((d) => {
                      const pos = d.pnl_dollar >= 0;
                      return (
                        <tr key={d.ticker} className="border-b border-zinc-900/50 hover:bg-zinc-900/30">
                          <td className="py-2 pr-3 font-bold text-zinc-100">{d.ticker}</td>
                          <td className="pr-3 text-right text-zinc-300">${d.avg_entry}</td>
                          <td className="pr-3 text-right text-zinc-300">${d.current_price}</td>
                          <td className={`pr-3 text-right font-bold ${pos ? "text-emerald-400" : "text-red-400"}`}>
                            {pos ? "+" : ""}{d.pnl_pct?.toFixed(2)}%
                          </td>
                          <td className={`pr-3 text-right font-bold ${pos ? "text-emerald-400" : "text-red-400"}`}>
                            {pos ? "+" : ""}${d.pnl_dollar?.toFixed(2)}
                          </td>
                          <td className="pr-3 text-right text-zinc-400">${d.invested?.toFixed(0)}</td>
                          <td className="pr-3 text-center text-zinc-400">{d.safety_orders_filled}/{d.max_so}</td>
                          <td className="pr-3 text-right text-zinc-400">{d.age_hours !== null ? `${d.age_hours.toFixed(0)}h` : "—"}</td>
                          <td className="pr-3 text-right text-zinc-500">+{d.tp_target_pct}%</td>
                          <td className="text-right text-zinc-400">{d.deals_closed} (${d.total_realized_pnl?.toFixed(0)})</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Recent Closed Deals */}
          {shadowData.recent_deals && shadowData.recent_deals.filter(d => ["TP_EXIT","TRAIL_EXIT","TIME_SAFETY_EXIT"].includes(d.type)).length > 0 && (
            <div className="mt-4">
              <div className="text-[11px] uppercase text-zinc-500 font-mono mb-2">✅ Recent Closed Deals</div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs font-mono">
                  <thead>
                    <tr className="text-zinc-500 border-b border-zinc-800">
                      <th className="text-left py-1 pr-3">Time</th>
                      <th className="text-left pr-3">Ticker</th>
                      <th className="text-left pr-3">Type</th>
                      <th className="text-right pr-3">Price</th>
                      <th className="text-right">P&L</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shadowData.recent_deals.filter(d => ["TP_EXIT","TRAIL_EXIT","TIME_SAFETY_EXIT"].includes(d.type)).slice(0,10).map((d, i) => (
                      <tr key={i} className="border-b border-zinc-900/30">
                        <td className="py-1.5 pr-3 text-zinc-400">{new Date(d.timestamp).toLocaleString().slice(0,16)}</td>
                        <td className="pr-3 text-zinc-100 font-bold">{d.ticker}</td>
                        <td className="pr-3 text-emerald-400">{d.type.replace("_EXIT","")}</td>
                        <td className="pr-3 text-right text-zinc-300">${d.price?.toFixed(2)}</td>
                        <td className={`text-right font-bold ${(d.pnl||0) >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                          {(d.pnl||0) >= 0 ? "+" : ""}${d.pnl?.toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}


      {/* NAV Chart */}
      {navSeries.length >= 2 && (
        <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-mono font-bold text-orange-400">NAV Trajectory</h3>
            <div className="text-[10px] text-zinc-500">
              {history.length} snapshots · since portfolio init
            </div>
          </div>
          <svg viewBox={`0 0 ${chartW} ${chartH}`} className="w-full">
            <rect x="0" y="0" width={chartW} height={chartH} fill="transparent" />
            {/* Initial reference line */}
            <line x1={padL} y1={initY} x2={chartW - padR} y2={initY}
                  stroke="#52525b" strokeDasharray="3 3" />
            <text x={chartW - padR - 70} y={initY - 3} fill="#71717a" fontSize="9" fontFamily="monospace">
              init ${initial.toFixed(0)}
            </text>
            {/* Area fill */}
            <defs>
              <linearGradient id="navGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#fb923c" stopOpacity="0.4" />
                <stop offset="100%" stopColor="#fb923c" stopOpacity="0.05" />
              </linearGradient>
            </defs>
            <path d={`${navPath} L ${(padL + (navSeries.length - 1) * xStep).toFixed(1)} ${(padT + innerH).toFixed(1)} L ${padL.toFixed(1)} ${(padT + innerH).toFixed(1)} Z`}
                  fill="url(#navGrad)" stroke="none" />
            <path d={navPath} fill="none" stroke="#fb923c" strokeWidth="2" />
            {/* Points */}
            {navSeries.map((p, i) => {
              const x = padL + i * xStep;
              const y = padT + innerH - ((p.value - yMin) / yRange) * innerH;
              return (
                <circle key={i} cx={x} cy={y} r={hoverIdx === i ? 4 : 2}
                        fill={hoverIdx === i ? "#fdba74" : "#fb923c"}
                        onMouseEnter={() => setHoverIdx(i)}
                        onMouseLeave={() => setHoverIdx(null)} />
              );
            })}
            {/* Y axis labels */}
            <text x={5} y={padT + 8} fill="#a1a1aa" fontSize="10" fontFamily="monospace">${yMax.toFixed(0)}</text>
            <text x={5} y={padT + innerH + 4} fill="#a1a1aa" fontSize="10" fontFamily="monospace">${yMin.toFixed(0)}</text>
            {/* Hover */}
            {hoverIdx !== null && hoverIdx < navSeries.length && (() => {
              const p = navSeries[hoverIdx];
              const x = padL + hoverIdx * xStep;
              const y = padT + innerH - ((p.value - yMin) / yRange) * innerH;
              return (
                <g pointerEvents="none">
                  <line x1={x} y1={padT} x2={x} y2={padT + innerH} stroke="#fb923c" strokeDasharray="2 2" opacity="0.5" />
                  <rect x={x + 8} y={y - 18} width="130" height="36" fill="#18181b" stroke="#fb923c" rx="3" />
                  <text x={x + 14} y={y - 5} fill="#fdba74" fontSize="11" fontFamily="monospace" fontWeight="bold">
                    ${p.value.toFixed(2)}
                  </text>
                  <text x={x + 14} y={y + 10} fill="#a1a1aa" fontSize="9" fontFamily="monospace">
                    {new Date(p.date).toLocaleString().slice(0, 16)}
                  </text>
                </g>
              );
            })()}
          </svg>
        </div>
      )}

      {/* LIVE DEAL CARDS — interactive with progress to TP */}
      {activeDeals.length > 0 && (
        <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <Activity className="w-4 h-4 text-orange-400 animate-pulse" />
            <h3 className="text-sm font-mono font-bold text-orange-400">🔵 LIVE DEAL CARDS — real-time P&L</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            {activeDeals.map((d) => {
              const pos = d.pnl_dollar >= 0;
              const flashClass = flashTickers[d.ticker] === "up" ? "ring-2 ring-emerald-400" :
                                 flashTickers[d.ticker] === "down" ? "ring-2 ring-red-400" : "";
              const tpProgress = Math.min(Math.max(d.pnl_pct / d.tp_target_pct * 100, 0), 100);
              return (
                <div key={d.ticker}
                     className={`bg-zinc-900/60 border ${pos ? "border-emerald-500/30" : "border-red-500/30"} rounded-lg p-3 transition-all duration-300 ${flashClass}`}>
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <div className="text-base font-bold font-mono text-zinc-100">{d.ticker}</div>
                      <div className="text-[10px] text-zinc-500">{d.qty?.toFixed(4)} shares · ${d.invested?.toFixed(0)} inv</div>
                    </div>
                    <div className={`text-right ${pos ? "text-emerald-400" : "text-red-400"}`}>
                      <div className="text-xl font-mono font-bold">{pos ? "+" : ""}{d.pnl_pct?.toFixed(2)}%</div>
                      <div className="text-xs font-mono">{pos ? "+" : ""}${d.pnl_dollar?.toFixed(2)}</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-[10px] font-mono mb-2">
                    <div>
                      <div className="text-zinc-500">Entry</div>
                      <div className="text-zinc-300">${d.avg_entry?.toFixed(2)}</div>
                    </div>
                    <div>
                      <div className="text-zinc-500">Live</div>
                      <div className={`font-bold ${flashTickers[d.ticker] === "up" ? "text-emerald-400" : flashTickers[d.ticker] === "down" ? "text-red-400" : "text-zinc-300"}`}>
                        ${d.current_price?.toFixed(2)}
                      </div>
                    </div>
                  </div>
                  {/* Progress to TP */}
                  <div className="mt-2">
                    <div className="flex justify-between text-[9px] text-zinc-500 mb-1">
                      <span>Progress to TP +{d.tp_target_pct}%</span>
                      <span>{tpProgress.toFixed(0)}%</span>
                    </div>
                    <div className="bg-zinc-800 rounded-full h-1.5 overflow-hidden">
                      <div className={`h-full ${pos ? "bg-emerald-500" : "bg-zinc-600"} transition-all duration-500`}
                           style={{width: `${tpProgress}%`}} />
                    </div>
                  </div>
                  {/* SO ladder + age */}
                  <div className="flex justify-between mt-2 text-[9px] font-mono text-zinc-500">
                    <span>SOs: {d.safety_orders_filled}/{d.max_so}</span>
                    <span>{d.age_hours !== null ? `${d.age_hours.toFixed(0)}h` : "—"} held</span>
                    <span>trail {d.trail_pct}%</span>
                  </div>
                  {/* Total realized for this bot */}
                  {d.deals_closed > 0 && (
                    <div className="mt-2 pt-2 border-t border-zinc-800 text-[9px] font-mono text-zinc-500">
                      Closed: {d.deals_closed} deals · ${d.total_realized_pnl?.toFixed(0)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* MACRO CONTEXT BAR */}
      {macroData && (
        <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-3">
          <div className="flex items-center gap-3 flex-wrap text-xs font-mono">
            <span className="text-zinc-500">MACRO:</span>
            {macroData.fear_greed !== undefined && (
              <span className={`${macroData.fear_greed >= 70 ? "text-red-400" : macroData.fear_greed <= 30 ? "text-purple-400" : "text-zinc-300"}`}>
                F&G {macroData.fear_greed}/100 ({macroData.fear_greed_class})
              </span>
            )}
            {macroData.btc_price && (
              <span className={`${macroData.btc_24h_pct >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                BTC ${macroData.btc_price?.toLocaleString()} ({macroData.btc_24h_pct >= 0 ? "+" : ""}{macroData.btc_24h_pct}%)
              </span>
            )}
            {macroData.sectors && Object.entries(macroData.sectors).slice(0, 6).map(([sym, data]) => (
              <span key={sym} className={`${data.change_pct >= 0 ? "text-emerald-500" : "text-red-500"}`}>
                {sym}: {data.change_pct >= 0 ? "+" : ""}{data.change_pct}%
              </span>
            ))}
          </div>
        </div>
      )}

      {/* EARNINGS WARNING (urgent earnings <7 days) */}
      {earnData && earnData.upcoming && earnData.upcoming.filter(e => e.warning === "URGENT").length > 0 && (
        <div className="bg-red-900/30 border-2 border-red-500/50 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xl">⚠</span>
            <h3 className="text-sm font-mono font-bold text-red-400">EARNINGS WITHIN 7 DAYS — DE-RISK DECISION</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {earnData.upcoming.filter(e => e.warning === "URGENT").map(e => (
              <div key={e.ticker} className="bg-zinc-900/60 rounded p-2">
                <div className="text-base font-bold text-red-400">{e.ticker}</div>
                <div className="text-xs text-zinc-300">In {e.days_to_earnings.toFixed(1)} days</div>
                <div className="text-[10px] text-zinc-500">{e.earnings_date.slice(0,10)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* SECTOR ROTATION TRACKER */}
      {secData && secData.sectors && (
        <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-lg">🔄</span>
            <h3 className="text-sm font-mono font-bold text-cyan-400">SECTOR ROTATION — 7d performance</h3>
            {secData.leader_7d && (
              <span className="text-[11px] font-mono">
                <span className="text-emerald-400">Leader: {secData.leader_7d}</span> ·
                <span className="text-red-400 ml-2">Laggard: {secData.laggard_7d}</span>
              </span>
            )}
          </div>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
            {Object.entries(secData.sectors)
              .sort(([,a], [,b]) => b.ret_7d - a.ret_7d)
              .map(([sym, d]) => (
                <div key={sym} className={`bg-zinc-900/60 border rounded p-2 ${
                  d.ret_7d > 2 ? "border-emerald-500/40" :
                  d.ret_7d < -2 ? "border-red-500/40" :
                  "border-zinc-700"
                }`}>
                  <div className="text-xs font-bold font-mono text-zinc-100">{sym}</div>
                  <div className="text-[9px] text-zinc-500">{d.label}</div>
                  <div className={`text-sm font-mono font-bold ${d.ret_7d >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                    {d.ret_7d >= 0 ? "+" : ""}{d.ret_7d}%
                  </div>
                  <div className="text-[9px] text-zinc-600">7d · 30d {d.ret_30d >= 0 ? "+" : ""}{d.ret_30d}%</div>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* PERFORMANCE ATTRIBUTION */}
      {attrData && attrData.fleet_winners && (
        <div className="bg-gradient-to-br from-emerald-900/20 to-zinc-900/40 border-2 border-emerald-500/30 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-lg">📊</span>
            <h3 className="text-sm font-mono font-bold text-emerald-400">PERFORMANCE ATTRIBUTION — winners & drivers</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="text-[11px] uppercase text-zinc-500 mb-2">🏆 TOP WINNERS</div>
              {attrData.fleet_winners.slice(0, 5).map((w, i) => (
                <div key={i} className="flex justify-between text-xs font-mono py-1 border-b border-zinc-800/50">
                  <span className="text-zinc-100">{w.ticker} <span className="text-zinc-500">({w.strategy})</span></span>
                  <span className={`font-bold ${(w.pnl_pct || 0) >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                    {(w.pnl_pct || 0) >= 0 ? "+" : ""}{(w.pnl_pct || 0).toFixed(2)}%
                  </span>
                </div>
              ))}
            </div>
            <div>
              <div className="text-[11px] uppercase text-zinc-500 mb-2">📉 LAGGARDS</div>
              {(attrData.fleet_losers || []).slice(0, 5).map((l, i) => (
                <div key={i} className="flex justify-between text-xs font-mono py-1 border-b border-zinc-800/50">
                  <span className="text-zinc-100">{l.ticker} <span className="text-zinc-500">({l.strategy})</span></span>
                  <span className={`font-bold ${(l.pnl_pct || 0) >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                    {(l.pnl_pct || 0) >= 0 ? "+" : ""}{(l.pnl_pct || 0).toFixed(2)}%
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* NEWS SENTIMENT per ticker */}
      {sentData && sentData.sentiments && sentData.sentiments.length > 0 && (
        <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-lg">📰</span>
            <h3 className="text-sm font-mono font-bold text-blue-400">NEWS SENTIMENT — {sentData.n} tickers tracked</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
            {sentData.sentiments.slice(0, 16).map(s => {
              const color = s.label === "BULLISH" ? "border-emerald-500/40 text-emerald-400" :
                            s.label === "BEARISH" ? "border-red-500/40 text-red-400" :
                            "border-zinc-700 text-zinc-400";
              return (
                <div key={s.ticker} className={`bg-zinc-900/60 border rounded p-2 ${color.split(" ")[0]}`}>
                  <div className="flex justify-between">
                    <span className="text-xs font-bold font-mono text-zinc-100">{s.ticker}</span>
                    <span className={`text-[10px] font-bold ${color.split(" ")[1]}`}>{s.label}</span>
                  </div>
                  <div className="text-[10px] text-zinc-500">
                    {s.n_articles} articles · score {s.avg_sentiment >= 0 ? "+" : ""}{s.avg_sentiment}
                  </div>
                  <div className="flex gap-2 mt-1 text-[9px] font-mono">
                    <span className="text-emerald-500">+{s.positive_count}</span>
                    <span className="text-red-500">−{s.negative_count}</span>
                    <span className="text-zinc-500">~{s.neutral_count}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* GOLD HUNTER — fresh signals, never sleeps */}
      {goldData && goldData.top && goldData.top.length > 0 && (
        <div className="bg-gradient-to-br from-yellow-900/30 to-zinc-900/50 border-2 border-yellow-500/50 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <span className="text-2xl">🌟</span>
              <h3 className="text-sm font-mono font-bold text-yellow-400">
                GOLD HUNTER — fresh signals · never sleeps
              </h3>
              <span className="text-[10px] text-zinc-500 font-mono">
                {goldData.n_alerts} alerts / {goldData.n_universe} hunted
              </span>
            </div>
            <div className="text-[10px] text-zinc-500 font-mono">
              {goldData.as_of ? new Date(goldData.as_of).toLocaleString().slice(0,16) : "—"}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {goldData.top.slice(0, 10).map((alert, idx) => (
              <div key={idx} className={`bg-zinc-900/60 border rounded-lg p-3 ${
                alert.total_score > 30 ? "border-yellow-400/60" :
                alert.total_score > 15 ? "border-orange-400/40" :
                "border-zinc-700"
              }`}>
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <div className="text-base font-bold font-mono text-yellow-400">
                      {idx === 0 && "👑 "}{alert.ticker}
                    </div>
                    <div className="text-[10px] text-zinc-500">{alert.n_signals} signals firing</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xl font-mono font-bold text-yellow-400">{alert.total_score}</div>
                    <div className="text-[9px] text-zinc-500">score</div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1">
                  {(alert.signals || []).map((s, i) => (
                    <span key={i} className={`text-[9px] font-mono px-1.5 py-0.5 rounded ${
                      s.type.includes("OVERSOLD") || s.type.includes("LOWER_BREAK") || s.type.includes("GAP_DOWN") ? "bg-purple-900/40 text-purple-300" :
                      s.type.includes("OVERBOUGHT") || s.type.includes("UPPER") || s.type.includes("52W_HIGH") ? "bg-orange-900/40 text-orange-300" :
                      s.type.includes("VOLUME") || s.type.includes("BIG_MOVE") ? "bg-yellow-900/40 text-yellow-300" :
                      "bg-zinc-800 text-zinc-400"
                    }`}>
                      {s.type.replace(/_/g, " ")} {s.intensity}
                    </span>
                  ))}
                </div>
                {alert.signals && alert.signals[0] && (
                  <div className="text-[10px] text-zinc-400 mt-2 font-mono italic">
                    {alert.signals[0].note}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ALPHA GAUNTLET — Strategy Tournament Leaderboard */}
      {gauntletData && gauntletData.strategies && gauntletData.strategies.length > 0 && (
        <div className="bg-gradient-to-br from-yellow-900/20 to-zinc-900/40 border-2 border-yellow-500/30 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-xl">🏆</span>
            <h3 className="text-sm font-mono font-bold text-yellow-400">
              ALPHA GAUNTLET — multi-strategy tournament
            </h3>
            <span className="text-[10px] text-zinc-500 font-mono">
              · 4 strategies competing for alpha · live leaderboard
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            {gauntletData.strategies.map((s, idx) => {
              const pnl = s.pnl_pct || 0;
              const positive = pnl >= 0;
              const positions = Object.keys(s.positions || {});
              const medal = idx === 0 ? "🥇" : idx === 1 ? "🥈" : idx === 2 ? "🥉" : "  ";
              return (
                <div key={s.strategy} className={`bg-zinc-900/60 border ${positive ? "border-emerald-500/30" : "border-red-500/30"} rounded-lg p-3`}>
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <div className="text-xs text-zinc-500 font-mono">{medal} #{idx+1}</div>
                      <div className="text-base font-bold font-mono text-zinc-100">{s.strategy}</div>
                    </div>
                    <div className={`text-right ${positive ? "text-emerald-400" : "text-red-400"}`}>
                      <div className="text-xl font-mono font-bold">{positive ? "+" : ""}{pnl.toFixed(2)}%</div>
                      <div className="text-xs font-mono">${s.current_nav?.toFixed(2)}</div>
                    </div>
                  </div>
                  <div className="text-[10px] text-zinc-500 font-mono">
                    Initial: ${s.initial_capital?.toFixed(0)} · Trades: {s.trades || 0}
                  </div>
                  {positions.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-zinc-800">
                      <div className="text-[10px] text-zinc-500 mb-1">Holdings:</div>
                      <div className="text-xs font-mono text-zinc-300">
                        {positions.slice(0, 3).join(", ")}
                        {positions.length > 3 && ` +${positions.length - 3}`}
                      </div>
                    </div>
                  )}
                  {s.recent_trades && s.recent_trades.length > 0 && (
                    <div className="mt-2 text-[9px] font-mono text-zinc-500">
                      Last: {s.recent_trades[0].type} {s.recent_trades[0].ticker} @ ${s.recent_trades[0].price?.toFixed(2)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div className="mt-3 text-[10px] text-zinc-600 font-mono">
            Strategies compete in parallel · winner = highest NAV after 30d · all paper-only
          </div>
        </div>
      )}

      {/* S&P 500 OPPORTUNITIES — pattern scanner output */}
      {oppsData && oppsData.top_50 && oppsData.top_50.length > 0 && (
        <div className="bg-gradient-to-br from-purple-900/20 to-zinc-900/40 border-2 border-purple-500/30 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Target className="w-5 h-5 text-purple-400" />
              <h3 className="text-sm font-mono font-bold text-purple-400">
                🎯 S&P 500 OPPORTUNITIES — pattern scanner ({oppsData.opportunities_count} hits / {oppsData.scanned} scanned)
              </h3>
            </div>
            <div className="text-[10px] text-zinc-500 font-mono">
              {oppsData.as_of ? new Date(oppsData.as_of).toLocaleString().slice(0,16) : "—"}
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs font-mono">
              <thead>
                <tr className="text-zinc-500 border-b border-zinc-800">
                  <th className="text-left py-2 pr-3">#</th>
                  <th className="text-left pr-3">Ticker</th>
                  <th className="text-left pr-3">Sector</th>
                  <th className="text-right pr-3">Price</th>
                  <th className="text-right pr-3">RSI</th>
                  <th className="text-right pr-3">52w DD</th>
                  <th className="text-left pr-3">Patterns</th>
                  <th className="text-right">Score</th>
                </tr>
              </thead>
              <tbody>
                {(oppsData.top_50 || []).slice(0, 25).map((o, i) => {
                  const rsiColor = o.rsi < 30 ? "text-purple-400" : o.rsi < 40 ? "text-orange-400" : "text-zinc-300";
                  const ddColor = o.drawdown_52w < -25 ? "text-red-400" : o.drawdown_52w < -10 ? "text-orange-400" : "text-zinc-300";
                  return (
                    <tr key={i} className="border-b border-zinc-900/50 hover:bg-zinc-900/30">
                      <td className="py-1.5 pr-3 text-zinc-500">{i+1}</td>
                      <td className="pr-3 font-bold text-zinc-100">{o.ticker}</td>
                      <td className="pr-3 text-zinc-500 truncate" style={{maxWidth: "120px"}}>{o.sector?.slice(0,18)}</td>
                      <td className="pr-3 text-right text-zinc-300">${o.price?.toFixed(2)}</td>
                      <td className={`pr-3 text-right font-bold ${rsiColor}`}>{o.rsi}</td>
                      <td className={`pr-3 text-right ${ddColor}`}>{o.drawdown_52w?.toFixed(0)}%</td>
                      <td className="pr-3 text-zinc-400" style={{fontSize: "9px"}}>
                        {Object.keys(o.patterns || {}).map(p => p.replace(/_/g," ")).join(", ")}
                      </td>
                      <td className="text-right font-bold text-purple-400">{o.total_score}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="text-[10px] text-zinc-600 font-mono text-center pt-2">
        Auto-refreshes every 10s · Bot heartbeat every 5min · S&P 500 patterns scan every 4h · paper-only
      </div>
    </div>
  );
}
